/**
 * The gap between "I have an account" and "I have a workspace".
 *
 * A brand-new account holds an ONBOARDING token, not an access token. It is a
 * different credential on purpose: the server's `verifyAccess` refuses any
 * token without an organization, so this one cannot reach tenant data even by
 * accident. It authorises exactly two things — finishing your profile, and
 * creating (or joining) your first company.
 *
 * Kept in sessionStorage rather than localStorage: an abandoned sign-up should
 * not still be sitting on the machine tomorrow, and it expires in an hour
 * server-side anyway.
 */

const KEY = 'wehi_onboarding';

export function setOnboardingToken(token: string): void {
  try {
    sessionStorage.setItem(KEY, token);
  } catch {
    /* private mode — the wizard still works for this page load */
  }
}

export function getOnboardingToken(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearOnboardingToken(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Same mount point as lib/api.ts — the ERP router lives under /api/erp. */
const BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/erp';

export class OnboardingError extends Error {
  // Plain fields, not constructor parameter properties: this project builds
  // with `erasableSyntaxOnly`, which forbids any TS syntax that emits runtime
  // code (parameter properties, enums, namespaces).
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'OnboardingError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Call an onboarding endpoint with the onboarding token.
 *
 * Deliberately NOT `lib/api.ts`: that helper attaches the access token and
 * silently refreshes it on a 401, which is exactly the wrong behaviour here —
 * there is no session to refresh, and mixing the two credentials is how one
 * ends up being accepted where the other was meant.
 */
export async function onboardingApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getOnboardingToken();
  if (!token) throw new OnboardingError('Your sign-up expired — start again', 401, 'NO_TOKEN');

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });

  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  } & Record<string, unknown>;

  if (!res.ok) {
    throw new OnboardingError(body.error ?? 'Something went wrong', res.status, body.code);
  }
  return body as T;
}

/** A workspace slug, as you type: lowercase, hyphens, nothing exotic. */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}
